import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { t } from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Folder, FolderOpen, File as FileIcon, ChevronDown, ChevronRight, Download, PanelLeft } from 'lucide-react';
import { RadixBreadcrumbs, RadixButton, RadixDrawer, RadixSelect, RadixSpinner } from '@/components/radix';
import { useMediaQuery } from '@/hooks/shared';
import { useFetchDoctorsList } from '@/hooks/api/doctorPortal';
import type { RootState } from '@/redux/slices/slices.types';
import type { GetApiDoctorsPortalDoctors200DoctorsItem } from '@/shared/api/models';
import { GetApiDoctorsPortalDoctorsStatus } from '@/shared/api/models/getApiDoctorsPortalDoctorsStatus';
import { api } from '@/utils/Api/POS';
import {
    clearDoctorInstructionsState,
    setInstructionsError,
    setInstructionsLoading,
    setInstructionsTree,
    setSelectedDoctorUuid as setSelectedDoctorUuidAction,
    type InstructionsDirectory,
    type InstructionsFile,
} from '@/redux/slices/Doctors';
import { cnMerge } from '@/utils/cnMerge';
import {
    buildBreadcrumbs,
    buildPathIds,
    DoctorDirectoryTreeResponse,
    flattenDirectoryTree,
} from './components/directory-tree.utils';
import InstructionsFolderSidebar from './components/InstructionsFolderSidebar';

const LINKED_DOCTORS_LIMIT = 100;
const STORAGE_KEY_LAST_DOCTOR = 'doctorInstructions_lastSelectedDoctor';

export default function DoctorInstructions() {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [selectedDoctorUuid, setSelectedDoctorUuidState] = useState<string>('');
    const [sidebarDrawerOpen, setSidebarDrawerOpen] = useState(false);
    const isDesktop = useMediaQuery('(min-width: 960px)');
    const hasAttemptedAutoSelect = useRef(false);

    const fetchParams = useMemo(
        () => ({
            page: 1,
            limit: LINKED_DOCTORS_LIMIT,
            status: GetApiDoctorsPortalDoctorsStatus.LINKED,
        }),
        [],
    );
    useFetchDoctorsList(fetchParams);

    const { doctors } = useSelector((state: RootState) => state.doctorsList);
    const instructionsState = useSelector((state: RootState) => state.doctorInstructions);

    const dirParam = searchParams.get('dir');
    const pathIds = useMemo(
        () =>
            dirParam
                ? dirParam
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean)
                : [],
        [dirParam],
    );
    const selectedDirectoryId = pathIds[pathIds.length - 1] ?? null;

    const linkedDoctorOptions = useMemo(
        () =>
            (doctors as GetApiDoctorsPortalDoctors200DoctorsItem[]).map((d) => ({
                value: d.uuid,
                label: `${d.firstName} ${d.lastName}`.trim() || d.email,
            })),
        [doctors],
    );

    const navigateWithDir = useCallback(
        (ids: string[]) => {
            const next = new URLSearchParams(location.search);
            if (ids.length > 0) {
                next.set('dir', ids.join(','));
            } else {
                next.delete('dir');
            }
            const search = next.toString();
            navigate(`${location.pathname}${search ? `?${search}` : ''}`, { replace: true });
        },
        [location.pathname, location.search, navigate],
    );

    const handleDoctorChange = useCallback(
        (doctorUuid: string) => {
            setSelectedDoctorUuidState(doctorUuid);
            dispatch(setSelectedDoctorUuidAction(doctorUuid || null));
            if (doctorUuid) {
                try {
                    localStorage.setItem(STORAGE_KEY_LAST_DOCTOR, doctorUuid);
                } catch {
                    // ignore
                }
            }

            if (!doctorUuid) {
                dispatch(clearDoctorInstructionsState());
                navigateWithDir([]);
                return;
            }

            dispatch(setInstructionsLoading(true));
        },
        [dispatch, navigateWithDir],
    );

    useEffect(() => {
        // Fetch directory tree when a doctor is selected and loading is requested.
        const loadDirectories = async () => {
            if (!instructionsState.selectedDoctorUuid || !instructionsState.loading) return;
            try {
                const res = await api.getApiDoctorsPortalDoctorsDoctorIdDirectories(
                    instructionsState.selectedDoctorUuid,
                );
                const tree = flattenDirectoryTree(res?.tree as DoctorDirectoryTreeResponse['tree']);
                dispatch(setInstructionsTree(tree));

                if (!dirParam && tree.roots.length > 0) {
                    navigateWithDir([tree.roots[0].directoryId]);
                }
            } catch (error) {
                dispatch(
                    setInstructionsError(
                        error instanceof Error ? error.message : 'Failed to load doctor instructions directories',
                    ),
                );
            }
        };

        void loadDirectories();
    }, [dispatch, dirParam, instructionsState.loading, instructionsState.selectedDoctorUuid, navigateWithDir]);

    useEffect(() => {
        const list = doctors as GetApiDoctorsPortalDoctors200DoctorsItem[];
        if (list.length === 0 || selectedDoctorUuid || hasAttemptedAutoSelect.current) return;
        hasAttemptedAutoSelect.current = true;
        let uuidToSelect: string | null = null;
        try {
            const stored = localStorage.getItem(STORAGE_KEY_LAST_DOCTOR);
            if (stored && list.some((d) => d.uuid === stored)) {
                uuidToSelect = stored;
            } else if (list.length > 0) {
                uuidToSelect = list[0].uuid;
            }
        } catch {
            if (list.length > 0) uuidToSelect = list[0].uuid;
        }
        if (uuidToSelect) {
            setSelectedDoctorUuidState(uuidToSelect);
            dispatch(setSelectedDoctorUuidAction(uuidToSelect));
            dispatch(setInstructionsLoading(true));
        }
    }, [doctors, selectedDoctorUuid, dispatch]);

    useEffect(() => {
        // Keep local state in sync if instructions state has a selected doctor (e.g. after refresh).
        if (instructionsState.selectedDoctorUuid && !selectedDoctorUuid) {
            setSelectedDoctorUuidState(instructionsState.selectedDoctorUuid);
        }
    }, [instructionsState.selectedDoctorUuid, selectedDoctorUuid]);

    const allDirs: InstructionsDirectory[] = useMemo(
        () => instructionsState.tree?.allDirs ?? [],
        [instructionsState.tree?.allDirs],
    );
    const roots: InstructionsDirectory[] = useMemo(
        () => instructionsState.tree?.roots ?? [],
        [instructionsState.tree?.roots],
    );
    const childrenByParent: Record<string, InstructionsDirectory[]> = useMemo(
        () => instructionsState.tree?.childrenByParent ?? {},
        [instructionsState.tree?.childrenByParent],
    );
    const filesByDirectoryId: Record<string, InstructionsFile[]> = useMemo(
        () => instructionsState.tree?.filesByDirectoryId ?? {},
        [instructionsState.tree?.filesByDirectoryId],
    );

    useEffect(() => {
        if (!instructionsState.loading && !dirParam && roots.length > 0) {
            navigateWithDir([roots[0].directoryId]);
        }
    }, [dirParam, instructionsState.loading, roots, navigateWithDir]);

    const breadcrumbDirs = useMemo(
        () => (pathIds.length > 0 ? buildBreadcrumbs(allDirs, pathIds) : []),
        [allDirs, pathIds],
    );

    const contentDirs = useMemo(
        () => (selectedDirectoryId ? (childrenByParent[selectedDirectoryId] ?? []) : []),
        [childrenByParent, selectedDirectoryId],
    );

    const contentFiles = useMemo(
        () => (selectedDirectoryId ? (filesByDirectoryId[selectedDirectoryId] ?? []) : []),
        [filesByDirectoryId, selectedDirectoryId],
    );

    const [expandedLocal, setExpandedLocal] = useState<Set<string>>(new Set());
    const expandedIds = expandedLocal;

    const pathIdsKey = pathIds.join(',');
    useEffect(() => {
        setExpandedLocal((prev) => {
            const next = new Set(prev);
            pathIds.slice(0, -1).forEach((id) => next.add(id));
            return next;
        });
    }, [pathIdsKey, pathIds]);

    const handleToggleExpand = useCallback((id: string) => {
        setExpandedLocal((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const handleSelectFolder = useCallback(
        (dir: InstructionsDirectory) => {
            const ids = buildPathIds(allDirs, dir.directoryId);
            navigateWithDir(ids);
            if (!isDesktop) setSidebarDrawerOpen(false);
        },
        [allDirs, navigateWithDir, isDesktop],
    );

    const renderTreeNode = useCallback(
        (dir: InstructionsDirectory, depth: number) => {
            const isExpanded = expandedIds.has(dir.directoryId);
            const children = childrenByParent[dir.directoryId] ?? [];
            const files = filesByDirectoryId[dir.directoryId] ?? [];
            const hasLoaded = dir.directoryId in childrenByParent || dir.directoryId in filesByDirectoryId;
            const hasChildren = hasLoaded ? children.length > 0 || files.length > 0 : true;

            const indent = depth * 2;

            return (
                <div key={dir.directoryId} className="flex flex-col">
                    <div
                        className={cnMerge(
                            'flex items-center gap-0.5 py-1 pr-2 rounded-md cursor-pointer group min-h-7',
                            selectedDirectoryId === dir.directoryId && 'bg-accent',
                        )}
                        // This moves the folder row to the right based on depth
                        style={{ paddingLeft: indent }}
                    >
                        <button
                            type="button"
                            className="p-0.5 shrink-0 rounded hover:bg-muted border-0 bg-transparent cursor-pointer"
                            onClick={() => handleToggleExpand(dir.directoryId)}
                            aria-expanded={hasChildren || !hasLoaded ? isExpanded : undefined}
                        >
                            {hasChildren || !hasLoaded ? (
                                isExpanded ? (
                                    <ChevronDown className="h-5 w-5 text-text-secondary" />
                                ) : (
                                    <ChevronRight className="h-5 w-5 text-text-secondary" />
                                )
                            ) : (
                                <span className="w-5 inline-block" aria-hidden />
                            )}
                        </button>

                        <button
                            type="button"
                            className="flex items-center gap-2 min-w-0 flex-1 text-left rounded py-0.5 pr-1 hover:bg-muted/50 border-0 bg-transparent cursor-pointer text-text-primary"
                            onClick={() => handleSelectFolder(dir)}
                        >
                            {isExpanded ? (
                                <FolderOpen className="h-5 w-5 shrink-0 text-amber-600" />
                            ) : (
                                <Folder className="h-5 w-5 shrink-0 text-amber-600" />
                            )}
                            <span className="truncate text-sm text-text-primary">{dir.name}</span>
                        </button>
                    </div>

                    {isExpanded && (
                        <div
                            className="relative border-0 border-l-[1px] border-solid border-border-default ml-2"
                            // This shifts the entire sub-tree container
                            style={{ marginLeft: indent }}
                        >
                            {/* Recursively render folders */}
                            {children.map((child) => renderTreeNode(child, depth + 1))}

                            {/* Render files with child-level indentation */}
                            {files.map((file) => (
                                <div
                                    key={file.fileId}
                                    className="flex items-center gap-2 py-1 rounded-md group min-h-7 cursor-default"
                                    // EXACT CALCULATION FOR FILES: (depth + 1) * 2 + 4
                                    style={{ paddingLeft: (depth + 1) * 2 + 4 }}
                                >
                                    <span className="w-4 shrink-0" aria-hidden />
                                    <FileIcon className="h-4 w-4 shrink-0 text-text-secondary" />
                                    <span className="truncate text-sm text-text-primary">{file.fileName}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        },
        [
            childrenByParent,
            expandedIds,
            filesByDirectoryId,
            handleSelectFolder,
            handleToggleExpand,
            selectedDirectoryId,
        ],
    );

    return (
        <section className="space-y-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-xl font-semibold text-text-primary m-0 md:mb-1">
                        {t('Doctors.InstructionsPageTitle')}
                    </h1>
                    <p className="text-sm text-text-secondary m-0">{t('Doctors.InstructionsDescription')}</p>
                </div>
                <RadixSelect
                    value={selectedDoctorUuid}
                    onValueChange={handleDoctorChange}
                    options={linkedDoctorOptions}
                    placeholder={t('Doctors.SelectLinkedDoctor')}
                    className="w-full sm:w-fit"
                />
            </div>
            {instructionsState.loading && selectedDoctorUuid && (
                <div className="mt-6 text-sm text-text-secondar flex items-center justify-center h-[80dvh] border-[1px] border-solid border-border-default rounded-md p-4 gap-2">
                    {t('Common.Loading')}
                    <RadixSpinner />
                </div>
            )}
            {!selectedDoctorUuid && (
                <div className="mt-6 text-sm text-text-secondary flex items-center justify-center h-[80dvh] border-[1px] border-solid border-border-default rounded-md p-4">
                    <p className="text-xl font-bold text-text-secondary text-center">
                        {t('Doctors.SelectDoctorToViewInstructions')}
                    </p>
                </div>
            )}
            {selectedDoctorUuid && !instructionsState.loading && instructionsState.error && (
                <div className="mt-6 text-sm text-red-600 flex items-center justify-center h-[80dvh] border-[1px] border-solid border-border-default rounded-md p-4">
                    <p className="text-xl font-bold text-text-secondary text-center">{instructionsState?.error}</p>
                </div>
            )}
            {!instructionsState.loading && !instructionsState.error && roots?.length === 0 && (
                <div className="mt-6 text-sm text-text-secondary flex items-center justify-center h-[80dvh] border-[1px] border-solid border-border-default rounded-md p-4">
                    <p className="text-xl font-bold text-text-secondary text-center">
                        {t('Doctors.NoInstructionsFound')}
                    </p>
                </div>
            )}
            {selectedDoctorUuid && !instructionsState.loading && !instructionsState.error && roots.length > 0 && (
                <div className="mt-6 flex gap-4 h-[calc(100vh-12rem)] min-h-[400px]">
                    {isDesktop ? (
                        <InstructionsFolderSidebar roots={roots} renderTreeNode={renderTreeNode} />
                    ) : (
                        <>
                            <RadixDrawer
                                open={sidebarDrawerOpen}
                                onOpenChange={setSidebarDrawerOpen}
                                position="left"
                                showCloseButton={true}
                            >
                                <InstructionsFolderSidebar
                                    roots={roots}
                                    renderTreeNode={renderTreeNode}
                                    className="w-full border-0 rounded-none bg-muted/30"
                                />
                            </RadixDrawer>
                        </>
                    )}
                    <div className="flex-1 border border-solid border-border-default rounded-lg overflow-hidden flex flex-col bg-muted/30 min-w-0">
                        <div className="p-3 border-0 border-b-[1px] border-solid border-border-default flex flex-col gap-2 shrink-0">
                            {!isDesktop && (
                                <RadixButton
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSidebarDrawerOpen(true)}
                                    className="self-start flex gap-2 items-center"
                                >
                                    <PanelLeft className="h-4 w-4 shrink-0 " />
                                    <span className="text-sm">{t('Doctors.InstructionsFolders')}</span>
                                </RadixButton>
                            )}
                            <RadixBreadcrumbs
                                items={breadcrumbDirs.map((dir, idx) => ({
                                    label: dir.name,
                                    onClick: () => navigateWithDir(pathIds.slice(0, idx + 1)),
                                    isCurrentPage: idx === breadcrumbDirs.length - 1,
                                }))}
                                className="flex overflow-x-auto scrollbar-hidden whitespace-nowrap"
                            />
                        </div>
                        <div className="flex-1 overflow-y-auto p-3">
                            {contentDirs.length === 0 && contentFiles.length === 0 && selectedDirectoryId && (
                                <div className="flex flex-col gap-4 items-center justify-center h-full">
                                    <p className="text-xl font-bold text-text-primary text-center">
                                        {t('Doctors.NoInstructionsItemsSelected')}
                                    </p>
                                </div>
                            )}
                            {contentDirs.length > 0 && (
                                <div className="flex flex-col gap-4 mb-2">
                                    {contentDirs.map((dir) => (
                                        <button
                                            key={dir.directoryId}
                                            type="button"
                                            className="flex items-center justify-start gap-2 p-3 rounded-md hover:bg-accent/50 w-full text-left border-[1px] border-solid border-border-default bg-transparent cursor-pointer text-text-primary"
                                            onClick={() => handleSelectFolder(dir)}
                                        >
                                            <Folder className="h-5 w-5 shrink-0 text-amber-500" />
                                            <span className="truncate text-sm">{dir.name}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {contentFiles.length > 0 && (
                                <div className="flex flex-col gap-2 mb-2">
                                    {contentFiles.map((file) => (
                                        <div
                                            key={file.fileId}
                                            className={cnMerge(
                                                'flex items-center justify-between gap-2 p-3 rounded-md w-full ',
                                                'border-[1px] border-solid border-border-default',
                                                'bg-transparent hover:bg-accent/50',
                                            )}
                                        >
                                            <span className="flex items-center gap-2">
                                                <FileIcon className="h-5 w-5 shrink-0 text-text-secondary" />
                                                <span className="truncate text-sm text-text-primary">
                                                    {file.fileName}
                                                </span>
                                            </span>

                                            <a
                                                href={file.fileUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="cursor-pointer text-text-secondary m-0 p-0 flex items-center justify-center"
                                            >
                                                <Download />
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
